import { Component } from '@theme/component';

/**
 * Wraps a hero video so it attempts to autoplay with sound, falling back to
 * muted autoplay when the browser blocks unmuted playback. Tapping the video
 * (or its audio indicator) toggles the audio on and off.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The hero video element.
 * @property {HTMLButtonElement} [audioIndicator] - The mute/unmute indicator button.
 *
 * @extends Component<Refs>
 */
export class HeroVideoComponent extends Component {
  requiredRefs = ['video'];

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;

    video.addEventListener('volumechange', () => this.#syncAudioIndicator());
    this.#syncAudioIndicator();

    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  toggleAudio() {
    this.refs.video.muted = !this.refs.video.muted;
  }

  #syncAudioIndicator() {
    const { video, audioIndicator } = this.refs;

    if (!audioIndicator) return;

    const [onIcon, offIcon] = audioIndicator.querySelectorAll('.hero-video-audio-indicator__icon');

    onIcon?.classList.toggle('hidden', video.muted);
    offIcon?.classList.toggle('hidden', !video.muted);

    audioIndicator.setAttribute(
      'aria-label',
      video.muted ? audioIndicator.dataset.unmuteLabel ?? '' : audioIndicator.dataset.muteLabel ?? ''
    );
  }
}

if (!customElements.get('hero-video-component')) {
  customElements.define('hero-video-component', HeroVideoComponent);
}
